import type { ConversionRecord } from '../types';

/**
 * Presentation logic for the conversion-history table (CONVERSIONS_SPEC.md §8.2).
 *
 * Split out of `ClientPage.tsx` so it is testable without mounting a page.
 * ⚠ **Corrected 2026-08-18:** the reason recorded here was *"vitest runs `environment: 'node'`
 * with no jsdom, so anything worth asserting has to be reachable without rendering"* — untrue
 * since 2026-07-31, and `src/test/renderWithProviders.tsx` is now the harness for anything that
 * does need a DOM. Extraction remains the right call for pure presentation logic; it is no
 * longer the only option. These three functions carry the decisions that would
 * actually mislead a bookkeeper if they were wrong — which field means "period", when a
 * number is unknown versus zero, and what an unparseable date renders as.
 */

/**
 * ISO instant → `yyyy-MM-dd HH:mm` (UTC).
 *
 * ⚠ Falls back to the RAW value rather than rendering `Invalid Date`. A malformed
 * timestamp is a data problem someone needs to see; "Invalid Date" hides which record it
 * came from and cannot be searched for.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

/**
 * The period column, which means different things per flow and must not pretend otherwise.
 *
 * Manual journals carry ONE posting date (`journalDate`); the bank flows carry the span
 * present in the file (`periodStart`/`periodEnd`). A single-day span collapses to one date
 * rather than rendering `2026-05-01 → 2026-05-01`, which reads as a bug.
 *
 * Returns an em-dash when neither applies — a conversion of an empty file genuinely has no
 * period, and the server records null rather than a default date for exactly that reason.
 */
export function describePeriod(
  record: Pick<ConversionRecord, 'journalDate' | 'periodStart' | 'periodEnd'>,
): string {
  if (record.journalDate) return record.journalDate;
  if (record.periodStart && record.periodEnd) {
    return record.periodStart === record.periodEnd
      ? record.periodStart
      : `${record.periodStart} → ${record.periodEnd}`;
  }
  // One end without the other is not a range and must not be shown as one.
  return '—';
}

/**
 * The totals column. Journals show debits/credits; bank flows show a line count.
 *
 * ⚠ `0` is a REAL answer and must not be swallowed by a truthiness check — a journal that
 * balances at zero, or a conversion of an empty file, is a thing that happened. Every test
 * for this function includes a zero case for that reason.
 */
export function describeTotals(
  record: Pick<ConversionRecord, 'totalDebit' | 'totalCredit' | 'lineCount'>,
): string {
  if (record.totalDebit != null && record.totalCredit != null) {
    return `${record.totalDebit.toFixed(2)} / ${record.totalCredit.toFixed(2)}`;
  }
  if (record.lineCount != null) return String(record.lineCount);
  return '—';
}

/**
 * Whether the retained file can be offered for re-download.
 *
 * A record with no `blobPath` is not corrupt: it is the recorded outcome of a conversion
 * whose file could not be retained. Offering a download that 404s reads to a bookkeeper as
 * data loss rather than as the storage hiccup it was, so the button is withheld instead.
 */
export function isDownloadable(record: Pick<ConversionRecord, 'blobPath'>): boolean {
  return typeof record.blobPath === 'string' && record.blobPath.trim() !== '';
}

/**
 * Hand a downloaded conversion to the browser as a file. One helper for the Conversions and Work tabs, so the two
 * download buttons over the same records cannot drift.
 */
export function saveDownloadedFile(result: { blob: Blob; filename: string }): void {
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
